import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect } from 'react';
import './PropertyWizard.css';

/**
 * Step 0: Ownership Verification
 *
 * Sub-steps:
 *   choose: "my property" or "someone else's property"
 *
 *   SELF flow:
 *     self-confirm: national_id already exists; show and approve
 *     self-enter: national_id missing; enter it, save to own profile, then confirm
 *
 *   OTHER flow:
 *     other-form: enter owner national_id and phone
 *     other-confirm: show resolved or created owner, approve
 *
 *   property-select: (TODO: gov API) for now just continue manually
 */

function OwnershipStep({ onSubmit, initialData = {}, onPreviousStep }) {
  const [subStep, setSubStep] = useState('choose');
  const [currentUser, setCurrentUser] = useState(null);
  const [resolvedOwner, setResolvedOwner] = useState(null);

  // Form fields
  const [selfNationalId, setSelfNationalId] = useState('');
  const [otherNationalId, setOtherNationalId] = useState('');
  const [otherPhone, setOtherPhone] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = () => localStorage.getItem('access_token');
  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token()}`,
  });

  // Load current user on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiRequest(apiRoutes.auth.currentUser, {
          headers: { 'Authorization': `Bearer ${token()}` },
        });
        if (res.ok) setCurrentUser(await res.json());
      } catch (e) {

      }
    };
    load();
  }, []);

  // ── SELF flow handlers ─────────────────────────────────────────────────────

  const handleChooseSelf = () => {
    setError(null);
    if (currentUser?.national_id) {
      // Already has national_id — go straight to confirm
      setResolvedOwner({
        id: currentUser.id,
        full_name: currentUser.full_name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username,
        national_id: currentUser.national_id,
        phone: currentUser.phone,
        created: false,
      });
      setSubStep('self-confirm');
    } else {
      // No national_id yet — ask user to enter it
      setSubStep('self-enter');
    }
  };

  const handleSelfNationalSubmit = async (e) => {
    e.preventDefault();
    const nid = selfNationalId.trim();
    if (!nid) { setError('کد ملی الزامی است'); return; }

    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest(apiRoutes.properties.saveSelfNationalId, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ national_id: nid }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.national_id || data.detail || 'خطا در ذخیره کد ملی');
        return;
      }
      // Update local currentUser and move to confirm
      setCurrentUser(prev => ({ ...prev, national_id: nid }));
      setResolvedOwner({
        id: data.id,
        full_name: data.full_name,
        national_id: data.national_id,
        phone: data.phone,
        created: false,
      });
      setSubStep('self-confirm');
    } catch {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // ── OTHER flow handlers ────────────────────────────────────────────────────

  const handleOtherFormSubmit = async (e) => {
    e.preventDefault();
    const nid = otherNationalId.trim();
    const phone = otherPhone.trim();
    if (!nid) { setError('کد ملی مالک الزامی است'); return; }
    if (!phone) { setError('شماره موبایل مالک الزامی است'); return; }

    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest(apiRoutes.properties.resolveOwner, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ national_id: nid, phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.national_id || data.phone || data.detail || 'خطا در بررسی اطلاعات');
        return;
      }
      setResolvedOwner(data);
      setSubStep('other-confirm');
    } catch {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // Shared confirmation before property selection.

  const handleConfirm = () => {
    setError(null);
    setSubStep('property-select');
  };

  const handlePropertyNotFound = () => {
    onSubmit({ owner_id: resolvedOwner.id });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (subStep === 'choose') {
    return (
      <div className="ownership-step">
        <div className="step-header">
          <h2>
          ملک را برای چه کسی ثبت می‌کنید؟
            </h2>

        </div>

        <div className="ownership-choice-grid">
          <button className="ownership-choice-card" onClick={handleChooseSelf}>
            <span className="choice-label">ملک خودم</span>
            <span className="choice-hint">مالک ملک خودم هستم</span>
          </button>

          <button className="ownership-choice-card" onClick={() => { setError(null); setSubStep('other-form'); }}>
            <span className="choice-label">ملک شخص دیگری</span>
            <span className="choice-hint">از طرف مالک دیگری ثبت می‌کنم</span>
          </button>
        </div>
      </div>
    );
  }

  // SELF — enter national_id (only shown when user has none)
  if (subStep === 'self-enter') {
    return (
      <div className="ownership-step">
        <div className="step-header">
          <h2>کد ملی شما</h2>
          <p className="step-description">
            کد ملی در پروفایل شما ثبت نشده است. لطفاً وارد کنید.
          </p>
        </div>

        <form onSubmit={handleSelfNationalSubmit} className="ownership-form">
          <div className="form-group">
            <label>کد ملی</label>
            <input
              type="text"
              value={selfNationalId}
              onChange={e => { setSelfNationalId(e.target.value); setError(null); }}
              placeholder="۱۰ رقم"
              maxLength={10}
              className="form-input"
              dir="ltr"
              autoFocus
            />
          </div>

          {error && <p className="error-message">{error}</p>}

          <div className="action-buttons-container">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'در حال ذخیره...' : 'تأیید و ادامه'}
            </button>
            <button type="button" className="back-button" onClick={() => { setError(null); setSubStep('choose'); }}>
              بازگشت
            </button>
          </div>
        </form>
      </div>
    );
  }

  // SELF — confirm own info
  if (subStep === 'self-confirm' && resolvedOwner) {
    return (
      <div className="ownership-step">
        <div className="step-header">
          <h2>تأیید اطلاعات مالک</h2>
          <p className="step-description">اطلاعات زیر را بررسی و تأیید کنید</p>
        </div>

        <OwnerInfoCard owner={resolvedOwner} />

        <div className="action-buttons-container">
          <button className="submit-button" onClick={handleConfirm}>
            تأیید و ادامه
          </button>
          <button className="back-button" onClick={() => { setError(null); setSubStep('choose'); }}>
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  // OTHER — enter national_id + phone
  if (subStep === 'other-form') {
    return (
      <div className="ownership-step">
        <div className="step-header">
          <h2>اطلاعات مالک</h2>
          <p className="step-description">کد ملی و شماره موبایل مالک را وارد کنید</p>
        </div>

        <form onSubmit={handleOtherFormSubmit} className="ownership-form">
          <div className="form-group">
            <label>کد ملی مالک</label>
            <input
              type="text"
              value={otherNationalId}
              onChange={e => { setOtherNationalId(e.target.value); setError(null); }}
              placeholder="۱۰ رقم"
              maxLength={10}
              className="form-input"
              dir="ltr"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>شماره موبایل مالک</label>
            <input
              type="tel"
              value={otherPhone}
              onChange={e => { setOtherPhone(e.target.value); setError(null); }}
              placeholder="09xxxxxxxxx"
              maxLength={11}
              className="form-input"
              dir="ltr"
            />
          </div>

          {error && <p className="error-message">{error}</p>}

          <div className="action-buttons-container">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? '...' : 'جستجو'}
            </button>
            <button type="button" className="back-button" onClick={() => { setError(null); setSubStep('choose'); }}>
              بازگشت
            </button>
          </div>
        </form>
      </div>
    );
  }

  // OTHER — confirm resolved owner
  if (subStep === 'other-confirm' && resolvedOwner) {
    return (
      <div className="ownership-step">
        <div className="step-header">
          <h2>تأیید اطلاعات مالک</h2>
          <p className="step-description">اطلاعات زیر را بررسی و تأیید کنید</p>
        </div>

        <OwnerInfoCard owner={resolvedOwner} />

        <div className="action-buttons-container">
          <button className="submit-button" onClick={handleConfirm}>
            تأیید و ادامه
          </button>
          <button className="back-button" onClick={() => { setError(null); setResolvedOwner(null); setSubStep('other-form'); }}>
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  // Property selection (government API — TODO)
  if (subStep === 'property-select') {
    return (
      <div className="ownership-step">
        <div className="step-header">
          <h2>انتخاب ملک</h2>
          <p className="step-description">ملک مورد نظر را انتخاب کنید</p>
        </div>

        <div className="property-not-found-section">
          <div className="not-found-notice">
            <p>در حال حاضر امکان دریافت لیست ملک از سامانه دولتی فراهم نیست.</p>
          </div>

          <button className="submit-button not-found-button" onClick={handlePropertyNotFound}>
            ملک مورد نظر یافت نشد — ادامه ثبت دستی
          </button>
        </div>

        <div className="action-buttons-container" style={{ marginTop: 16 }}>
          <button className="back-button" onClick={() => setSubStep(resolvedOwner ? 'self-confirm' : 'other-confirm')}>
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// Shared owner info display card
function OwnerInfoCard({ owner }) {
  return (
    <div className="info-card">
      <div className="info-content">
        <h3>اطلاعات مالک</h3>
        <div className="info-row">
          <label>نام:</label>
          <span className="info-value">{owner.full_name || '—'}</span>
        </div>
        <div className="info-row">
          <label>کد ملی:</label>
          <span className="info-value" dir="ltr">{owner.national_id}</span>
        </div>
        <div className="info-row">
          <label>شماره موبایل:</label>
          <span className="info-value" dir="ltr">{owner.phone || '—'}</span>
        </div>
        {owner.created && (
          <div className="verification-notice" style={{ marginTop: 12 }}>
            <div className="notice-content">
              <p>حساب کاربری جدیدی برای این مالک ایجاد شد.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OwnershipStep;
